import { useMemo } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useCatalogData } from "../components/CatalogDataContext";
import {
  formatNormalized,
  formatUsd,
  isHostAggregator,
  scenarioCost,
  statusLabel,
  unitLabel,
} from "../lib/format";
import { useCompare } from "../components/CompareContext";
import { ModalityBadge, ConfidenceBadge } from "../components/Badges";
import { Button } from "../components/ui/button";
import { CompareSpider } from "../components/ui/CompareSpider";
import {
  MIXED_MODALITY_COMPARE_MSG,
  isMixedModalityCompare,
} from "../lib/compareModality";
import { StatusDot } from "../components/ui/StatusDot";

export function ComparePage() {
  const [params] = useSearchParams();
  const { selected, toggle, clear } = useCompare();
  const { models, providers, meta } = useCatalogData();
  const getModel = (id: string) => models.find((m) => m.id === id);
  const getProvider = (id: string) => providers.find((p) => p.id === id);
  const ids = useMemo(() => {
    const fromUrl = params.get("ids")?.split(",").filter(Boolean) ?? [];
    return [...new Set([...fromUrl, ...selected])].slice(0, 4);
  }, [params, selected]);
  const compared = ids
    .map((id) => getModel(id))
    .filter((m): m is NonNullable<typeof m> => Boolean(m));

  const mixedModality = isMixedModalityCompare(compared);

  const cheapestImageId = useMemo(() => {
    let best: { id: string; cost: number } | null = null;
    for (const m of compared) {
      const c = scenarioCost(m, "1_image");
      if (c == null) continue;
      if (!best || c < best.cost) best = { id: m.id, cost: c };
    }
    return best?.id ?? null;
  }, [compared]);

  const cheapestVideo5sId = useMemo(() => {
    let best: { id: string; cost: number } | null = null;
    for (const m of compared) {
      const c = scenarioCost(m, "5s_video");
      if (c == null) continue;
      if (!best || c < best.cost) best = { id: m.id, cost: c };
    }
    return best?.id ?? null;
  }, [compared]);

  const scrapeLabel = meta.lastPriceScrapeAt
    ? new Date(meta.lastPriceScrapeAt).toLocaleString("en-IN", {
        timeZone: "Asia/Calcutta",
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      }) + " IST"
    : null;

  return (
    <div>
      <p className="eyebrow">01 / COMPARE</p>
      <div className="page-header-row">
        <div>
          <h1 className="tl-title" style={{ fontSize: "clamp(1.65rem, 3vw, 2.4rem)" }}>
            Capability spider
            <span className="ctx"> · costs</span>
          </h1>
          <p className="page-sub" style={{ marginBottom: 0, marginTop: "0.55rem" }}>
            Select 2–4 models from the catalog or open with <code>?ids=</code>. Costs
            use normalized units (~1024² image or $/sec video, prefer 720p).
            {scrapeLabel ? (
              <>
                {" "}
                Last price scrape: <span className="mono">{scrapeLabel}</span>
              </>
            ) : null}
          </p>
        </div>
        {compared.length >= 2 ? (
          <StatusDot kind="live" label={`${compared.length} MODELS · ACTIVE`} />
        ) : null}
      </div>
      {compared.length >= 2 && (cheapestImageId || cheapestVideo5sId) && (
        <div className="compare-highlights">
          {cheapestImageId && (
            <span className="pill cheap">
              Cheapest 1 image:{" "}
              <strong>{getModel(cheapestImageId)?.name}</strong> (
              {formatUsd(scenarioCost(getModel(cheapestImageId)!, "1_image"))})
            </span>
          )}
          {cheapestVideo5sId && (
            <span className="pill cheap">
              Cheapest 5s video:{" "}
              <strong>{getModel(cheapestVideo5sId)?.name}</strong> (
              {formatUsd(scenarioCost(getModel(cheapestVideo5sId)!, "5s_video"))})
            </span>
          )}
        </div>
      )}
      {compared.length < 2 && (
        <div className="panel panel-pad spider-empty" style={{ marginBottom: "1.15rem" }}>
          <p className="muted" style={{ margin: 0 }}>
            Pick at least two models to unlock the capability spider and cost
            grid. <Link to="/">Browse catalog</Link>
          </p>
          <div className="caps" style={{ marginTop: "0.85rem" }}>
            {models.slice(0, 12).map((m) => (
              <button
                key={m.id}
                type="button"
                className="btn ghost sm"
                onClick={() => toggle(m.id)}
                style={{ marginRight: 4, marginBottom: 4, maxWidth: "none" }}
              >
                {selected.includes(m.id) ? "✓ " : "+ "}
                {m.name}
              </button>
            ))}
          </div>
        </div>
      )}
      {compared.length > 0 && (
        <>
          <div style={{ marginBottom: "0.85rem" }}>
            <Button variant="ghost" size="sm" type="button" onClick={clear}>
              Clear selection
            </Button>
          </div>

          {mixedModality ? (
            <div className="panel panel-pad spider-empty" style={{ marginBottom: "1rem" }}>
              <p className="form-error" role="alert" style={{ margin: 0 }}>
                {MIXED_MODALITY_COMPARE_MSG}
              </p>
            </div>
          ) : (
            compared.length >= 2 && <CompareSpider models={compared} />
          )}

          <div className="compare-grid" id="compare-cost-grid">
            {compared.map((m) => {
              const p = getProvider(m.providerId);
              const priceText = formatNormalized(m);
              const hasPrice = m.pricing.normalizedUsd != null && priceText !== "—";
              const host = isHostAggregator(m.providerId);
              const imgCost = scenarioCost(m, "1_image");
              const vidCost = scenarioCost(m, "5s_video");
              const isCheapestImg = m.id === cheapestImageId;
              const isCheapestVid = m.id === cheapestVideo5sId;
              return (
                <div
                  className={`compare-col${isCheapestImg || isCheapestVid ? " is-cheap" : ""}`}
                  key={m.id}
                >
                  <h3>
                    <Link to={`/models/${m.id}`}>{m.name}</Link>
                  </h3>
                  <div className="compare-badges">
                    {host ? (
                      <span className="pill host">Host / aggregator</span>
                    ) : (
                      <span className="pill direct">Direct API</span>
                    )}
                    {isCheapestImg && <span className="pill cheap">Best 1 img</span>}
                    {isCheapestVid && <span className="pill cheap">Best 5s</span>}
                  </div>
                  <div
                    className="compare-price"
                    style={
                      !hasPrice
                        ? { color: "var(--faint)", opacity: 0.5 }
                        : undefined
                    }
                  >
                    {priceText}
                    {hasPrice && (
                      <span className="compare-unit">
                        {unitLabel(m.pricing.normalizedUnit)}
                      </span>
                    )}
                  </div>
                  {m.pricing.assumptions && (
                    <div className="compare-assumptions muted">
                      {m.pricing.assumptions}
                    </div>
                  )}
                  <div className="compare-row">
                    <span className="label">Provider</span>
                    <span>
                      {p ? (
                        <Link to={`/providers/${p.id}`}>{p.name}</Link>
                      ) : (
                        m.providerId
                      )}
                    </span>
                  </div>
                  <div className="compare-row">
                    <span className="label">Modality</span>
                    <ModalityBadge value={m.modality} />
                  </div>
                  <div className="compare-row">
                    <span className="label">1 image</span>
                    <span className={`mono${isCheapestImg ? " cheap-text" : ""}`}>
                      {imgCost != null ? formatUsd(imgCost) : "—"}
                    </span>
                  </div>
                  <div className="compare-row">
                    <span className="label">5s video</span>
                    <span className={`mono${isCheapestVid ? " cheap-text" : ""}`}>
                      {vidCost != null ? formatUsd(vidCost) : "—"}
                    </span>
                  </div>
                  <div className="compare-row">
                    <span className="label">Raw</span>
                    <span>{m.pricing.raw}</span>
                  </div>
                  <div className="compare-row">
                    <span className="label">Top tier</span>
                    <span className="mono">
                      {m.pricing.tiers[0]
                        ? `${formatUsd(m.pricing.tiers[0].amountUsd)} ${m.pricing.tiers[0].unit}`
                        : "—"}
                    </span>
                  </div>
                  <div className="compare-row">
                    <span className="label">Status</span>
                    <span>{statusLabel(m.status)}</span>
                  </div>
                  <div className="compare-row">
                    <span className="label">Confidence</span>
                    <ConfidenceBadge value={m.pricing.confidence} />
                  </div>
                  <div className="compare-row">
                    <span className="label">Capabilities</span>
                    <div className="caps">
                      {m.capabilities.map((c) => (
                        <span className="cap" key={c} style={{ maxWidth: "none" }}>
                          {c}
                        </span>
                      ))}
                    </div>
                  </div>
                  <div className="compare-row">
                    <span className="label">Verified</span>
                    <span className="mono">{m.lastVerified}</span>
                  </div>
                  <div className="compare-row">
                    <span className="label">Docs</span>
                    <a href={m.docsUrl} target="_blank" rel="noreferrer">
                      Open →
                    </a>
                  </div>
                  <button
                    type="button"
                    className="btn ghost sm"
                    style={{ marginTop: "0.85rem" }}
                    onClick={() => toggle(m.id)}
                  >
                    Remove
                  </button>
                </div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}
